import { ActionTypes, type ConfigUpdate, type TableAction } from './actions'
import { keysOf } from '@betterplace/utils'
import type { ColumnConfig } from '../../types'
import type { ColumnField } from '../../server/types'
import type { EntityState, NormalizedColumnConfig, TableState } from './types'

function isSameEntity<T extends Record<string, unknown>>(
  a: T,
  b: T,
  columns: Array<keyof T>,
  getRowId: (item: T) => string
) {
  const res = getRowId(a) === getRowId(b)
  if (!res) return false
  for (const field of columns) {
    const aField = a[field]
    const bField = b[field]
    if (!aField || !bField) return false
    if (typeof aField === 'object' && typeof bField === 'object') {
      return isSameEntity(
        aField as Record<string, unknown>,
        bField as Record<string, unknown>,
        keysOf(aField),
        () => '1'
      )
    } else if (aField !== bField) {
      return false
    }
  }
  return true
}

function isSameColumnConfig<T extends Record<string, unknown>>(
  a: NormalizedColumnConfig<T>,
  b: NormalizedColumnConfig<T>
) {
  for (const field of keysOf(a).concat(keysOf(b))) {
    if (a[field] !== b[field]) return false
  }
  return true
}

function updateEntityState<T extends Record<string, unknown>>(
  state: EntityState<T>,
  nextData: T[],
  idFunction: (item: T) => string,
  isSame: (a: T, b: T) => boolean
): [EntityState<T>, hasChanged: boolean] {
  const entities = { ...state.entities }
  const idx: string[] = []
  let anyChanged = false
  let idxChanged = state.idx.length !== nextData.length
  for (let i = 0; i < nextData.length; i++) {
    const item = nextData[i]!
    const oldId = state.idx[i]!
    const id = idFunction(item)
    if (oldId !== id) {
      idxChanged = true
      anyChanged = true
    }
    idx.push(id)
    if (!entities[id] || !isSame(entities[id], item)) {
      anyChanged = true
      entities[id] = item
    }
  }
  if (!anyChanged) return [state, false]
  return [{ entities, idx: idxChanged ? idx : state.idx }, anyChanged || idxChanged]
}

function updateDataEntities<T extends Record<string, unknown>>(state: TableState<T>, nextData: T[]) {
  function isSame(a: T, b: T) {
    return isSameEntity(a, b, state.config.columns.idx, state.config.getRowId)
  }
  const [data, hasChanged] = updateEntityState(state.data, nextData, state.config.getRowId, isSame)
  if (!hasChanged) return state
  return {
    ...state,
    data,
  }
}

function normalizeColumnConfig<T extends Record<string, unknown>, K extends ColumnField<T>>(
  config: ColumnConfig<T, K>
): NormalizedColumnConfig<T, K> {
  return {
    ...config,
    width: config.width ?? 0,
    minWidth: config.minWidth ?? 0,
    maxWidth: config.maxWidth ?? Number.POSITIVE_INFINITY,
    resizable: config.resizable ?? false,
    lastResizable: undefined,
  }
}

function updateConfig<T extends Record<string, unknown>>(state: TableState<T>, nextConfig: ConfigUpdate<T>) {
  const next = nextConfig.columns.map((c) => normalizeColumnConfig(c))
  const lastResizable = next.findLast((c) => c.resizable)
  if (lastResizable) {
    lastResizable.lastResizable = true
  }
  const [columns, columnsHaveChanged] = updateEntityState(
    state.config.columns,
    next,
    (column) => column.field,
    isSameColumnConfig
  )
  let hasChanged = columnsHaveChanged
  if (!hasChanged) {
    for (const key of keysOf(nextConfig)) {
      if (key === 'columns') continue
      if (state.config[key] !== nextConfig[key]) {
        hasChanged = true
        break
      }
    }
  }
  if (!hasChanged) return state
  return {
    ...state,
    config: { ...state.config, ...nextConfig, columns },
  }
}

export default function reducer<T extends Record<string, unknown>>(
  state: TableState<T>,
  action: TableAction<T>
): TableState<T> {
  switch (action.type) {
    case ActionTypes.Init: {
      const { data, config } = action.payload
      return updateDataEntities(updateConfig(getInitialState(), config), data)
    }
    case ActionTypes.SetData: {
      return updateDataEntities(state, action.payload)
    }
    case ActionTypes.SetConfig: {
      return updateConfig(state, action.payload)
    }
    default:
      return state
  }
}

export function getInitialState<T extends Record<string, unknown>>(): TableState<T> {
  return {
    data: { entities: {}, idx: [] as string[] },
    config: { columns: { entities: {}, idx: [] as string[] }, getRowId: (_: T) => '' },
  }
}
